import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import axios from 'axios'
import VehicleDropdown from '../../shared/common-dropdown/VehicleDropdown'
import PaymentTypeDropdown from '../../shared/common-dropdown/PaymentTypeDropdown'
import VehicleSalesGrid from './VehicleSalesGrid'
import PurchaseVehicleSelectionModal from './PurchaseVehicleSelectionModal'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import { fetchEnquiryById } from '../../utils/enquiryApi'
import { Calendar } from 'primereact/calendar'
import { useMasterData } from '../../utils/useMasterData'
import FinanceDetails from '../../shared/finance/FinanceDetails'

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const INITIAL_HEADER_STATE = {
  CustomerName: '',
  MobileNumber: '',
  EmailId: '',
  SalesDate: getTodayDate(),
  Remarks: '',
  EnquiryId: null,
}

const INITIAL_DETAIL_STATE = {
  id: null,
  SalesRegisterDetailId: null,
  VehicleId: '',
  VehicleLabel: '',
  VehicleCost: '',
  PaymentType: 'cash',
  PaymentTypeLabel: 'Cash',
  DownPayment: '',
  EMITenure: '',
  EMIAmount: '',
  ProcessingFees: '',
  InterestRate: '',
  ChassisNo: '',
  EngineNo: '',
  Selected: false
}

function AddSalesRegister({ sendDataToParent, onLoad, initialValues = null, onLoanSummary, onRegisterLoanSummaryApply }) {
  const [headerState, setHeaderState] = useState(INITIAL_HEADER_STATE)
  const [detailForm, setDetailForm] = useState(INITIAL_DETAIL_STATE)
  const [detailEntries, setDetailEntries] = useState([])
  const [vehicleError, setVehicleError] = useState('')
  const [duplicateError, setDuplicateError] = useState('')
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [purchaseVehicleId, setPurchaseVehicleId] = useState(null)
  const [purchaseVehicleLabel, setPurchaseVehicleLabel] = useState('')
  const [purchaseTargetRowId, setPurchaseTargetRowId] = useState(null)
  const initialValuesKeyRef = useRef(null)
  const [enquiryOptions, setEnquiryOptions] = useState([])
  const [selectedEnquiry, setSelectedEnquiry] = useState(null)
  const [loanSummaryError, setLoanSummaryError] = useState('')
  const [financePartners, setFinancePartners] = useState([])
  const [financePartnersError, setFinancePartnersError] = useState('')
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null)
  const skipDetailSyncRef = useRef(false)
  const { items: interestRateItems, loading: interestRateLoading } = useMasterData('interestRates')
  const [accessoriesItems, setAccessoriesItems] = useState([])
  const [accessoriesLoading, setAccessoriesLoading] = useState(false)
  const [accessoriesError, setAccessoriesError] = useState('')

  const interestRateOptions = useMemo(() => {
    return (interestRateItems || []).map((item) => ({
      value: item.ValueName ?? '',
      label: item.DisplayName || item.ValueName || '',
      id: item.MasterValueId
    }))
  }, [interestRateItems])

  const selectedInterestRate = useMemo(() => {
    if (!detailForm.InterestRate) return null
    const valueStr = detailForm.InterestRate.toString()
    return interestRateOptions.find((opt) => opt.value?.toString() === valueStr) || null
  }, [detailForm.InterestRate, interestRateOptions])

  const fetchFinancePartners = useCallback(async () => {
    try {
      setFinancePartnersError('')
      const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      setFinancePartners(rows)
    } catch (error) {
      setFinancePartners([])
      setFinancePartnersError('Unable to load finance partners.')
    }
  }, [])

  const fetchAccessories = useCallback(async () => {
    try {
      setAccessoriesLoading(true)
      setAccessoriesError('')
      const response = await axios.get(`${getApiBaseUrl()}/handoveritems/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const accessoryRows = rows.filter((item) => (item.HandoverItemType || '').toLowerCase() === 'accessories')
      setAccessoriesItems(accessoryRows)
    } catch (error) {
      setAccessoriesItems([])
      setAccessoriesError('Unable to load accessories.')
    } finally {
      setAccessoriesLoading(false)
    }
  }, [])
  const updateHeaderField = useCallback((name, value) => {
    setHeaderState((prev) => ({ ...prev, [name]: value }))
  }, [sendDataToParent])

  const handleHeaderChange = (e) => {
    const { name, value } = e.currentTarget
    updateHeaderField(name, value)
  }

  const handleSalesDateChange = (event) => {
    const dateValue = event?.value
    if (!dateValue) {
      updateHeaderField('SalesDate', '')
      return
    }
    const year = dateValue.getFullYear()
    const month = String(dateValue.getMonth() + 1).padStart(2, '0')
    const day = String(dateValue.getDate()).padStart(2, '0')
    updateHeaderField('SalesDate', `${year}-${month}-${day}`)
  }

  const handleDetailField = (name, value, options = {}) => {
    setDetailForm((prev) => {
      if (options.skipParent) {
        skipDetailSyncRef.current = true
      }
      return { ...prev, [name]: value }
    })
  }

  const handleVehicleChange = (option) => {
    const value = option ? option.value : ''
    const label = option ? option.label : ''
    handleDetailField('VehicleId', value, { skipParent: true })
    handleDetailField('VehicleLabel', label, { skipParent: true })
    setVehicleError('')
    setDuplicateError('')
    if (value) {
      setPurchaseVehicleId(value)
      setPurchaseVehicleLabel(label)
      setPurchaseTargetRowId(null)
      setShowPurchaseModal(true)
    }
  }

  const handlePaymentTypeChange = (option) => {
    const value = option ? option.value : ''
    const label = option ? option.label : ''
    handleDetailField('PaymentType', value)
    handleDetailField('PaymentTypeLabel', label, { skipParent: true })
    if (value !== 'loan') {
      handleDetailField('DownPayment', '', { skipParent: true })
      handleDetailField('EMITenure', '', { skipParent: true })
      handleDetailField('EMIAmount', '', { skipParent: true })
      handleDetailField('ProcessingFees', '', { skipParent: true })
      handleDetailField('InterestRate', '', { skipParent: true })
      setSelectedFinancePartnerId(null)
    }
  }

  const handleFinancePartnerClick = (partner) => {
    if (!partner) return
    const rate = partner.InterestRate ?? ''
    const downPayment = partner.MinimumDownPayment ?? ''
    handleDetailField('InterestRate', rate === null ? '' : rate.toString())
    handleDetailField('DownPayment', downPayment === null ? '' : downPayment.toString())
    const key = partner.FinancePartnerId ?? partner.FinancePartnerName ?? null
    setSelectedFinancePartnerId(key)
  }

  useEffect(() => {
    fetchAccessories()
  }, [fetchAccessories])

  const openPurchaseModalForRow = (row) => {
    if (!row) return
    setPurchaseVehicleId(row.VehicleId || null)
    setPurchaseVehicleLabel(row.VehicleLabel || row.Vehicle || '')
    setPurchaseTargetRowId(row.id || null)
    setShowPurchaseModal(true)
  }

  const handlePurchaseContinue = (row) => {
    if (!row) return
    if (purchaseTargetRowId) {
      updateDetailEntries((prev) => prev.map((entry) => (
        entry.id === purchaseTargetRowId
          ? {
            ...entry,
            ChassisNo: row.ChassisNo || entry.ChassisNo,
            EngineNo: row.EngineNo || entry.EngineNo,
            VehicleCost: row.VehicleCost ?? entry.VehicleCost
          }
          : entry
      )))
    } else {
      handleDetailField('ChassisNo', row.ChassisNo || '', { skipParent: true })
      handleDetailField('EngineNo', row.EngineNo || '', { skipParent: true })
      if (row.VehicleCost !== undefined && row.VehicleCost !== null) {
        handleDetailField('VehicleCost', row.VehicleCost, { skipParent: true })
      }
    }
    setShowPurchaseModal(false)
  }

  const closePurchaseModal = () => {
    setShowPurchaseModal(false)
  }

  const resetDetailForm = useCallback(() => {
    setDetailForm(INITIAL_DETAIL_STATE)
    setVehicleError('')
    setDuplicateError('')
    setSelectedFinancePartnerId(null)
  }, [])

  const updateDetailEntries = useCallback((updater) => {
    setDetailEntries((prev) => {
      const next = updater(prev)
      sendDataToParent((parentPrev) => ({ ...parentPrev, SalesRegisterDetails: next }))
      return next
    })
  }, [sendDataToParent])

  const addOrUpdateDetail = (e) => {
    e?.preventDefault()
    if (!detailForm.VehicleId) {
      setVehicleError('Vehicle is required')
      return
    }

    const duplicate = detailEntries.some((entry) => entry.VehicleId === detailForm.VehicleId && entry.id !== detailForm.id)
    if (duplicate) {
      setDuplicateError('An entry for this vehicle already exists.')
      return
    }

    const payload = {
      ...detailForm,
      id: detailForm.id || detailForm.SalesRegisterDetailId || Date.now(),
      Vehicle: detailForm.VehicleLabel || detailForm.Vehicle,
      PaymentType: detailForm.PaymentTypeLabel || detailForm.PaymentType,
      Selected: true
    }

    updateDetailEntries((prev) => {
      const next = detailForm.id
        ? prev.map((row) => (row.id === detailForm.id ? payload : row))
        : [...prev, payload]
      return next
    })

    resetDetailForm()
  }


  const handleEntrySelection = (row, isSelected) => {
    if (!row) return
    updateDetailEntries((prev) => prev.map((entry) => (entry.id === row.id ? { ...entry, Selected: isSelected } : entry)))
    if (isSelected) {
      openPurchaseModalForRow(row)
    }
  }

  const handleEntryFieldChange = (rowId, fieldName, value) => {
    updateDetailEntries((prev) => prev.map((entry) => (entry.id === rowId ? { ...entry, [fieldName]: value } : entry)))
  }

  const handleDeleteDetail = (row) => {
    updateDetailEntries((prev) => prev.filter((entry) => entry.id !== row.id))
  }

  useEffect(() => {
    const ADD_MODE_KEY = '__add__'
    const incomingKey = initialValues?.SalesRegisterId ?? (initialValues ? '__static__' : null)

    if (initialValues) {
      if (initialValuesKeyRef.current === incomingKey) return

      const header = {
        CustomerName: initialValues.CustomerName ?? '',
        MobileNumber: initialValues.MobileNumber ?? '',
        EmailId: initialValues.EmailId ?? '',
        SalesDate: initialValues.SalesDate ?? '',
        Remarks: initialValues.Remarks ?? '',
        EnquiryId: initialValues.EnquiryId ?? null,
      }
      setHeaderState({ ...INITIAL_HEADER_STATE, ...header })
      const details = (initialValues.SalesRegisterDetails || []).map((d) => ({
        ...d,
        id: d.id || d.SalesRegisterDetailId || Date.now() + Math.random(),
        VehicleLabel: d.VehicleLabel || d.Vehicle || d.VehicleNameValue || '',
        PaymentType: d.PaymentType,
        Selected: d.Selected !== undefined ? d.Selected : true
      }))
      setDetailEntries(details)
      sendDataToParent((prev) => ({ ...prev, ...header, SalesRegisterDetails: details }))
      resetDetailForm()
      initialValuesKeyRef.current = incomingKey
      return
    }

    if (initialValuesKeyRef.current === ADD_MODE_KEY) return

    onLoad?.(undefined)
    setHeaderState({ ...INITIAL_HEADER_STATE })
    setDetailEntries([])
    resetDetailForm()
    sendDataToParent((prev) => ({ ...prev, ...INITIAL_HEADER_STATE, SalesRegisterDetails: [] }))
    initialValuesKeyRef.current = ADD_MODE_KEY
  }, [initialValues, onLoad, resetDetailForm, sendDataToParent])

  const financeFieldsVisible = useMemo(() => detailForm.PaymentType === 'loan', [detailForm.PaymentType])
  const isEnquirySelected = Boolean(headerState.EnquiryId)
  const isEditMode = Boolean(initialValues?.SalesRegisterId)
  const hideAddForm = isEnquirySelected && !isEditMode

  const parsedSalesDate = useMemo(() => {
    if (!headerState.SalesDate) return null
    const date = headerState.SalesDate instanceof Date
      ? headerState.SalesDate
      : new Date(headerState.SalesDate)
    return Number.isNaN(date.getTime()) ? null : date
  }, [headerState.SalesDate])

  const parsedFinanceValues = useMemo(() => {
    const parseNumber = (value) => {
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : 0
    }

    return {
      vehicleCost: parseNumber(detailForm.VehicleCost),
      downPayment: parseNumber(detailForm.DownPayment),
      tenureMonths: parseInt(detailForm.EMITenure, 10) || 0,
      processingFees: parseNumber(detailForm.ProcessingFees),
      interestRate: parseNumber(detailForm.InterestRate)
    }
  }, [detailForm.VehicleCost, detailForm.DownPayment, detailForm.EMITenure, detailForm.ProcessingFees, detailForm.InterestRate])

  const calculateLoanSummary = () => {
    const { vehicleCost, downPayment, tenureMonths, processingFees, interestRate } = parsedFinanceValues

    if (!vehicleCost) throw new Error('Enter vehicle cost to calculate loan summary.')
    if (tenureMonths <= 0) throw new Error('Enter EMI tenure (in months).')
    if (interestRate <= 0) throw new Error('Enter annual interest rate.')

    const principal = Math.max(vehicleCost - downPayment, 0) + processingFees
    const monthlyRate = interestRate / 12 / 100
    const factor = Math.pow(1 + monthlyRate, tenureMonths)
    const emi = monthlyRate === 0 || !Number.isFinite(factor)
      ? principal / tenureMonths
      : (principal * monthlyRate * factor) / (factor - 1)

    return {
      vehicleCost,
      downPayment,
      processingFees,
      interestRate,
      tenureMonths,
      loanAmount: principal,
      monthlyEmi: Number((emi || 0).toFixed(2)),
      totalPayable: Number(((emi || 0) * tenureMonths).toFixed(2))
    }
  }

  const handleLoanSummaryClick = () => {
    try {
      const summary = calculateLoanSummary()
      setLoanSummaryError('')
      if (typeof onLoanSummary === 'function') {
        onLoanSummary(summary)
      }
    } catch (err) {
      setLoanSummaryError(err.message || 'Unable to calculate loan summary.')
    }
  }

  const applyLoanSummaryEmi = useCallback((summary) => {
    if (!summary) {
      return
    }
    const emiValue = summary.monthlyEmi ?? ''
    handleDetailField('EMIAmount', emiValue !== '' ? String(emiValue) : '', { skipParent: false })
  }, [handleDetailField])

  useEffect(() => {
    if (typeof onRegisterLoanSummaryApply === 'function') {
      onRegisterLoanSummaryApply(applyLoanSummaryEmi)
      return () => onRegisterLoanSummaryApply(null)
    }
  }, [applyLoanSummaryEmi, onRegisterLoanSummaryApply])

  useEffect(() => {
    sendDataToParent((parentPrev) => ({ ...parentPrev, ...headerState }))
  }, [headerState, sendDataToParent])

  useEffect(() => {
    if (skipDetailSyncRef.current) {
      skipDetailSyncRef.current = false
      return
    }
    sendDataToParent((parentPrev) => ({ ...parentPrev, CurrentDetail: detailForm }))
  }, [detailForm, sendDataToParent])

  useEffect(() => {
    fetchFinancePartners()
  }, [fetchFinancePartners])

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/enquiry/getall`, {
          params: { UserId: getStorageValues('userId'), IsForDropodown: '1' }
        })
        const rows = response?.data?.data
        const list = Array.isArray(rows) ? rows : []
        const opts = list.map((row) => {
          const enquiryNumber = row.EnquiryNumber
            || (() => {
              const d = row.CreatedDate ? new Date(row.CreatedDate) : null
              const mmdd = d && !isNaN(d.getTime())
                ? `${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate() + 0).padStart(2, '0')}`
                : '0000'
              const paddedId = String(row.EnquiryId ?? '').padStart(4, '0')
              return `ENQ/${mmdd}/${paddedId}`
            })()
          const name = row.CustomerName ? ` (${row.CustomerName})` : ''
          return {
            value: row.EnquiryId,
            label: `${enquiryNumber}${name}`,
            enquiryNumber,
            customerName: row.CustomerName || '',
            mobile: row.MobileNo || ''
          }
        })
        setEnquiryOptions(opts)
      } catch (error) {
        console.error('Failed to load enquiries', error)
      }
    }
    fetchEnquiries()
  }, [])

  useEffect(() => {
    if (headerState.EnquiryId && enquiryOptions.length > 0 && !selectedEnquiry) {
      const match = enquiryOptions.find((opt) => opt.value === headerState.EnquiryId)
      if (match) setSelectedEnquiry(match)
    }
  }, [enquiryOptions, headerState.EnquiryId, selectedEnquiry])

  const handleEnquirySelect = async (option) => {
    setSelectedEnquiry(option)
    const enquiryId = option ? option.value : null
    updateHeaderField('EnquiryId', enquiryId)

    if (option) {
      try {
        const data = await fetchEnquiryById({ userId: getStorageValues('userId'), enquiryId })
        const name = option.customerName || data?.header?.CustomerName || ''
        const mobile = option.mobile || data?.header?.MobileNo || ''
        setHeaderState((prev) => ({ ...prev, CustomerName: name, MobileNumber: mobile }))
        sendDataToParent((prev) => ({ ...prev, CustomerName: name, MobileNumber: mobile, EnquiryId: enquiryId }))

        const detailRows = (data?.details || []).map((d) => ({
          id: d.SalesRegisterDetailId || d.id || Date.now() + Math.random(),
          SalesRegisterDetailId: d.SalesRegisterDetailId || null,
          VehicleId: d.VehicleId,
          VehicleLabel: d.VehicleLabel || d.VehicleNameValue || d.VehicleName || '',
          VehicleCost: d.VehicleCost,
          PaymentType: d.PaymentType,
          DownPayment: d.DownPayment,
          EMITenure: d.EMITenure,
          EMIAmount: d.EMIAmount,
          ProcessingFees: d.ProcessingFees,
          InterestRate: d.InterestRate || '',
          ChassisNo: d.ChassisNo || '',
          EngineNo: d.EngineNo || '',
          PaymentTypeValue: d.PaymentType,
          VehicleNameValue: d.VehicleNameValue || d.VehicleLabel || '',
          Selected: false,
        }))
        setDetailEntries(detailRows)
        sendDataToParent((prev) => ({ ...prev, SalesRegisterDetails: detailRows }))
      } catch (error) {
        console.error('Failed to load enquiry details', error)
      }
    } else {
      setDetailEntries([])
      sendDataToParent((prev) => ({ ...prev, SalesRegisterDetails: [], CustomerName: '', MobileNumber: '' }))
      setHeaderState((prev) => ({ ...prev, CustomerName: '', MobileNumber: '' }))
    }
  }

  return (
    <>
      <div className="row">
        <div className="col-md-12">

          <div className="card-header">
            <div className="card-title"></div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-9 col-sm-9 col-12 mb-3">
                <label className="form-label">From Enquiry</label>
                <Select
                  value={selectedEnquiry}
                  onChange={handleEnquirySelect}
                  options={enquiryOptions}
                  placeholder="Select enquiry"
                  isClearable
                />
              </div>
              <div className="col-lg-3 col-sm-3 col-12 mb-3">
                <label className="form-label">Sales Date</label>
                <Calendar
                  name="SalesDate"
                  value={parsedSalesDate}
                  onChange={handleSalesDateChange}
                  maxDate={new Date()}
                  dateFormat="dd-mm-yy"
                  showIcon
                  inputStyle={{ height: '40px' }}
                />
                
              </div>
              <div className="col-lg-9 col-sm-9 col-12 mb-3">
                <label className="form-label">Customer Name<span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerName"
                  value={headerState.CustomerName}
                  onChange={handleHeaderChange}
                />
              </div>
              <div className="col-lg-3 col-sm-3 col-12 mb-3">
                <label className="form-label">Mobile No</label>
                <input
                  type="text"
                  className="form-control"
                  name="MobileNumber"
                  value={headerState.MobileNumber}
                  onChange={handleHeaderChange}
                />
              </div>


            </div>

            {!hideAddForm && (
              <>
                <div className="row">
                  <div className="col-lg-6 col-sm-6 col-12 mb-3">
                    <VehicleDropdown
                      onChangeDropdown={handleVehicleChange}
                      selectedValue={detailForm.VehicleId}
                    />
                    {vehicleError && <div className="text-danger small mt-1">{vehicleError}</div>}
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <label className="form-label">Vehicle Cost</label>
                    <input
                      type="text"
                      className="form-control"
                      name="VehicleCost"
                      value={detailForm.VehicleCost}
                      onChange={(e) => handleDetailField('VehicleCost', e.currentTarget.value)}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <PaymentTypeDropdown
                      onChangeDropdown={handlePaymentTypeChange}
                      selectedValue={detailForm.PaymentType}
                    />
                  </div>

                  {financeFieldsVisible && (
                    
                    <div className="col-12">
                      <FinanceDetails
                        financePartners={financePartners}
                        financePartnersError={financePartnersError}
                        selectedFinancePartnerId={selectedFinancePartnerId}
                        onFinancePartnerClick={handleFinancePartnerClick}
                        downPaymentValue={detailForm.DownPayment}
                        emiTenureValue={detailForm.EMITenure}
                        processingFeesValue={detailForm.ProcessingFees}
                        interestRateValue={detailForm.InterestRate}
                        emiAmountValue={detailForm.EMIAmount}
                        onDownPaymentChange={(e) => handleDetailField('DownPayment', e.currentTarget.value)}
                        onEmiTenureChange={(e) => handleDetailField('EMITenure', e.currentTarget.value)}
                        onProcessingFeesChange={(e) => handleDetailField('ProcessingFees', e.currentTarget.value)}
                        onInterestRateChange={(option) => handleDetailField('InterestRate', option ? option.value : '')}
                        onEmiAmountChange={(e) => handleDetailField('EMIAmount', e.currentTarget.value)}
                        interestRateOptions={interestRateOptions}
                        interestRateLoading={interestRateLoading}
                        selectedInterestRate={selectedInterestRate}
                        onCalculateEmi={handleLoanSummaryClick}
                        loanSummaryError={loanSummaryError}
                      />
                    </div>
                  )}
                </div>
                <div className="enq-form mb-4">
                  <button type="button" className="btn btn-cancel me-2" onClick={resetDetailForm}>
                    {detailForm.id ? 'Cancel Edit' : 'Reset'}
                  </button>
                  <button type="button" className="btn btn-submit" onClick={addOrUpdateDetail}>
                    {detailForm.id ? 'Update Vehicle' : 'Add Vehicle'}
                  </button>
                  {duplicateError && <div className="text-danger small mt-2">{duplicateError}</div>}
                </div>
              </>
            )}

              <div className="card-body">
                <nav className="nav nav-style-6 nav-pills mb-3 nav-justified d-sm-flex d-block" role="tablist">
                  <a className="nav-link active" data-bs-toggle="tab" role="tab" aria-current="page" href="#nav-products-justified" aria-selected="true">VEHICLE SALES DETAILS</a>
                  <a className="nav-link" data-bs-toggle="tab" role="tab" href="#nav-cart-justified" aria-selected="false" tabIndex="-1">VEHICLE ACCESSORIES </a>

                </nav>
                <div className="tab-content">
                  <div className="tab-pane text-muted active show" id="nav-products-justified" role="tabpanel">
                    <div className="col-12">
                      <VehicleSalesGrid
                        data={detailEntries}
                        onSelectEntry={handleEntrySelection}
                        onFieldChange={handleEntryFieldChange}
                        onDelete={handleDeleteDetail}
                      />
                    </div>
                  </div>
                  <div className="tab-pane text-muted" id="nav-cart-justified" role="tabpanel">
                    <div className="acces-box">
                      <div className="acess">
                        {accessoriesLoading && (
                          <p className="col-12 text-muted">Loading accessories…</p>
                        )}
                        {!accessoriesLoading && accessoriesItems?.length === 0 && (
                          <p className="col-12 text-muted">No accessories configured.</p>
                        )}
                        {accessoriesError && (
                          <p className="col-12 text-danger">{accessoriesError}</p>
                        )}
                        {(accessoriesItems || []).map((item) => (
                          <p className="col-md-2" key={item.HandoverItemId || item.HandoverItemTitle}>
                            {(item.HandoverItemTitle || '').toUpperCase()}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 col-sm-12 col-12 mb-3">
                  <label className="form-label">Remarks</label>
                  <input
                    type="text"
                    className="form-control"
                    name="Remarks"
                    value={headerState.Remarks}
                    onChange={handleHeaderChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <PurchaseVehicleSelectionModal
          show={showPurchaseModal}
          onClose={closePurchaseModal}
          onContinue={handlePurchaseContinue}
          vehicleId={purchaseVehicleId}
          vehicleLabel={purchaseVehicleLabel}
          excludeKeys={detailEntries.map((entry) => `${(entry.EngineNo || '').toString().trim()}|${(entry.ChassisNo || '').toString().trim()}`)}
        />
    </>
  )
}

export default AddSalesRegister
